import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { AlarmSetting } from 'ops/common/models/alarm-setting';
import { Channel, ChannelFns, FlagOption } from 'ops/common/models/channel';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationPromptService } from '@service/confirmation-prompt.service';
import {
    ObjectSchema,
    alarmSettingValidator,
    AlarmSettingValidationField,
} from 'ops/common/validators';
import { lastValueFrom } from 'rxjs';
import { ChannelService } from '@service/channel.service';
import { Mimic } from 'ops/common/models/mimic';
import { TrendGraphView } from 'ops/common/models/trend-graph-view';
import { ElementSettings } from 'ops/common/models/editor-viewer-element';
import { NavigationService } from '@service/navigation.service';
import { TrendGraphViewsService } from '@service/trend-graph-views.service';
import { MimicService } from '@service/mimic.service';
import { ElementSettingsService } from '@service/element-settings.service';
import { AlarmSettingFormComponent } from '@components/alarm-setting-form/alarm-setting-form.component';
import { AuthService } from '@service/auth.service';
import { ChannelFormatterService } from '@service/channel-formatter.service';
import { DeviceFlagsService } from '@service/device-flags.service';

@Component({
    selector: 'app-channel-settings-form',
    templateUrl: './channel-settings-form.component.html',
    styleUrls: ['./channel-settings-form.component.scss'],
    standalone: false,
})
export class ChannelSettingsFormComponent implements OnChanges {
    readonly ADDRESS_STRING = 'FCU';

    TAB_ALARM = 0;
    TAB_ABOUT = 1;

    @ViewChildren('alarmSettingForm') alarmSettingForm: AlarmSettingFormComponent[];
    @ViewChild('globalRepose', { static: false }) globalReposeInput: MatSlideToggle;

    @Output() onSubmit = new EventEmitter<Channel>();
    @Output() onUpdateChannelList = new EventEmitter<Channel>();
    @Output() onAdvancedSettings = new EventEmitter<Channel>();
    @Output() onDeleteAlarm = new EventEmitter<Channel>();

    @Output() onCloseClicked = new EventEmitter();
    @Input() channel: Channel;
    @Input() value: number = 0;

    mimics: Mimic[] = [];
    trendGraphs: TrendGraphView[] = [];
    elementSettings: ElementSettings[];
    vesselMimics: Mimic[];

    status: FlagOption = FlagOption.UNDEFINED;
    statusText: string = '';

    validators: { [key: string]: ObjectSchema<AlarmSetting> } = {
        veryLow: null,
        low: null,
        high: null,
        veryHigh: null,
    };

    openTab = this.TAB_ALARM;

    userPermission: boolean = false;
    scaleDecimalPlaces = 0;

    currentFlag: FlagOption = FlagOption.UNDEFINED;

    constructor(
        private translate: TranslateService,
        private confirmationPromptService: ConfirmationPromptService,
        private channelService: ChannelService,
        private navigationService: NavigationService,
        private graphService: TrendGraphViewsService,
        private mimicService: MimicService,
        private elementSettingsService: ElementSettingsService,
        private authService: AuthService,
        private channelFormatterService: ChannelFormatterService,
        private flagsService: DeviceFlagsService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.authService.currentUser$.subscribe(async (user) => {
            this.userPermission = await this.authService.checkChannelSettingPermission(
                user.userId,
                this.channel.groupId,
            );
            this.cdr.detectChanges();
        });
    }

    async ngOnChanges(changes: SimpleChanges) {
        if (changes.channel && this.channel) {
            let newValidators = {} as { [key: string]: ObjectSchema<AlarmSetting> };

            newValidators.veryLow = alarmSettingValidator(
                this.channel,
                AlarmSettingValidationField.ALARM_VERY_LOW,
            );
            newValidators.low = alarmSettingValidator(
                this.channel,
                AlarmSettingValidationField.ALARM_LOW,
            );
            newValidators.high = alarmSettingValidator(
                this.channel,
                AlarmSettingValidationField.ALARM_HIGH,
            );
            newValidators.veryHigh = alarmSettingValidator(
                this.channel,
                AlarmSettingValidationField.ALARM_VERY_HIGH,
            );
            newValidators.feedback = alarmSettingValidator(
                this.channel,
                AlarmSettingValidationField.ALARM_SETTING_FEEDBACK_ERROR,
            );
            newValidators.sensorFail = alarmSettingValidator(
                this.channel,
                AlarmSettingValidationField.ALARM_SETTING_SENSOR_FAIL,
            );
            newValidators.alarmSetting = alarmSettingValidator(
                this.channel,
                AlarmSettingValidationField.ALARM_SETTING,
            );

            const graphs = await lastValueFrom(this.graphService.getAll());
            this.setGraphs(graphs);

            this.elementSettings = await this.elementSettingsService.getAll();
            this.vesselMimics = await this.mimicService.getAll();
            this.setMimics();

            this.validators = newValidators;
            this.scaleDecimalPlaces = Math.log10(this.channel.fcuData.scale);

            this.updateDisplayToLatestValue();
            const userId = this.authService.getCurrentUserId();
            this.userPermission = await this.authService.checkChannelSettingPermission(
                userId,
                this.channel.groupId,
            );

            this.cdr.detectChanges();
        }

        if (this.value || this.value === 0) {
            this.updateDisplayToLatestValue();
        }
    }

    updateDisplayToLatestValue() {
        this.status = this.flagsService.getStatus(this.channel);
        this.statusText = ChannelFns.getStatusString(this.status);
    }

    async onGlobalReposeClicked(isChecked: boolean) {
        const reposeState = isChecked;

        const oldChannel = this.cloneChannel();

        if (this.channel.fcuData.digitalAlarmSetting.enable)
            this.channel.fcuData.digitalAlarmSetting.manualReposeFlag = reposeState;
        if (this.channel.fcuData.alarmSettingFeedbackError.enable)
            this.channel.fcuData.alarmSettingFeedbackError.manualReposeFlag = reposeState;
        if (this.channel.fcuData.alarmSettingVeryHigh.enable)
            this.channel.fcuData.alarmSettingVeryHigh.manualReposeFlag = reposeState;
        if (this.channel.fcuData.alarmSettingHigh.enable)
            this.channel.fcuData.alarmSettingHigh.manualReposeFlag = reposeState;
        if (this.channel.fcuData.alarmSettingLow.enable)
            this.channel.fcuData.alarmSettingLow.manualReposeFlag = reposeState;
        if (this.channel.fcuData.alarmSettingVeryLow.enable)
            this.channel.fcuData.alarmSettingVeryLow.manualReposeFlag = reposeState;

        try {
            await lastValueFrom(this.channelService.update(this.channel));
            this.onUpdateChannelList.emit(this.channel);
        } catch (err) {
            this.channel = oldChannel;
        }
    }

    emitSubmit(alarmSettingKey: string, alarmSetting: AlarmSetting) {
        this.createAlarmCallback(alarmSettingKey, this.onSubmit)(alarmSetting);
    }

    emitDelete(alarmSettingKey: string, alarmSetting: AlarmSetting) {
        this.confirmationPromptService.onOptionSelected().subscribe((confirm) => {
            if (confirm) {
                this.createAlarmCallback(alarmSettingKey, this.onDeleteAlarm)(alarmSetting);
            }
        });

        this.confirmationPromptService.showPrompt({
            mainLabel: this.translate.instant('forms.channel.alarm.delete-confirmation-prompt'),
            confirmLabel: this.translate.instant('forms.channel.alarm.delete-confirmation-button'),
            cancelLabel: this.translate.instant('forms.channel.alarm.delete-cancel-button'),
        });
    }

    createAlarmCallback(alarmSettingKey: string, event: EventEmitter<Channel>) {
        return (alarmSetting: AlarmSetting) => {
            const newChannel = this.cloneChannel();
            newChannel.fcuData[alarmSettingKey] = alarmSetting;
            event.emit(newChannel);
        };
    }

    closePreviewForm() {
        this.alarmSettingForm.forEach((alarmSettingForm) => alarmSettingForm.closeComponent());
    }

    cloneChannel(): Channel {
        return JSON.parse(JSON.stringify(this.channel));
    }

    emitClose() {
        this.onCloseClicked.emit();
    }

    emitAdvancedDetails() {
        this.onAdvancedSettings.emit(this.channel);
    }

    selectTab(tab: number) {
        this.openTab = tab;
    }

    transform(value: number) {
        const transformed = ChannelFns.transformValue(this.channel, value);
        return typeof transformed === 'number'
            ? transformed.toFixed(this.scaleDecimalPlaces)
            : transformed;
    }

    getScale() {
        return this.channel.fcuData.scale;
    }
    isDigital() {
        return ChannelFns.isDigital(this.channel);
    }
    isAnalog() {
        return ChannelFns.isAnalog(this.channel);
    }
    isRunningHour() {
        return ChannelFns.isRunningHour(this.channel);
    }

    getTag() {
        return this.channel.opsData.tag;
    }
    getName() {
        return this.channel.opsData.name;
    }
    getValue() {
        return this.transform(this.value);
    }
    getRawValue() {
        return this.value;
    }
    getUnits() {
        return this.channel.opsData.measurementUnit;
    }
    getMeasurableMin() {
        const scale = this.channel.fcuData.scale;
        const decimalPlaces = scale.toString().length - 1;
        const value = this.channel.fcuData.measurableRange.min / Math.pow(10, decimalPlaces);
        return value.toFixed(decimalPlaces);
    }
    getMeasurableMax() {
        const scale = this.channel.fcuData.scale;
        const decimalPlaces = scale.toString().length - 1;
        const value = this.channel.fcuData.measurableRange.max / Math.pow(10, decimalPlaces);
        return value.toFixed(decimalPlaces);
    }

    getGlobalReposeValue(): boolean {
        let manualReposeFlag: boolean = false;

        if (this.channel.fcuData.digitalAlarmSetting.enable)
            manualReposeFlag = this.channel.fcuData.digitalAlarmSetting.manualReposeFlag;
        if (this.channel.fcuData.alarmSettingFeedbackError.enable)
            manualReposeFlag = this.channel.fcuData.alarmSettingFeedbackError.manualReposeFlag;
        if (this.channel.fcuData.alarmSettingVeryHigh.enable)
            manualReposeFlag = this.channel.fcuData.alarmSettingVeryHigh.manualReposeFlag;
        if (this.channel.fcuData.alarmSettingHigh.enable)
            manualReposeFlag = this.channel.fcuData.alarmSettingHigh.manualReposeFlag;
        if (this.channel.fcuData.alarmSettingLow.enable)
            manualReposeFlag = this.channel.fcuData.alarmSettingLow.manualReposeFlag;
        if (this.channel.fcuData.alarmSettingVeryLow.enable)
            manualReposeFlag = this.channel.fcuData.alarmSettingVeryLow.manualReposeFlag;

        return manualReposeFlag;
    }

    getChannelType() {
        return this.channelFormatterService.getChannelType(this.channel);
    }

    getFcuNum() {
        return this.channelFormatterService.getFcuNum(this.channel);
    }

    getInputAddress() {
        return this.channelFormatterService.getInputAddress(this.channel);
    }

    getOutputSignalType() {
        return this.channelFormatterService.getOutputSignalType(this.channel);
    }

    getOutputAddress() {
        return this.channelFormatterService.getOutputAddress(this.channel);
    }

    setMimics() {
        const mimicIdsSet = new Set();
        this.mimics = [];
        for (let i = 0; i < this.elementSettings?.length; i++) {
            if (this.isInMimic(this.channel, this.elementSettings[i])) {
                mimicIdsSet.add(this.elementSettings[i].mimicId);
            }
        }

        this.vesselMimics.forEach((mimic) => {
            if (mimicIdsSet.has(mimic.id)) {
                this.mimics.push(mimic);
            }
        });
    }

    isInMimic(channel: Channel, elementSetting: ElementSettings) {
        if (channel.fcuData.fcuChannelId.toString() === elementSetting?.channelTag) {
            return true;
        } else if (
            channel.fcuData.fcuChannelId.toString() === elementSetting?.disabledStatus?.channelTag
        ) {
            return true;
        } else if (
            channel.fcuData.fcuChannelId.toString() === elementSetting?.flashingStatus?.channelTag
        ) {
            return true;
        }
        return false;
    }

    setGraphs(graphs: TrendGraphView[]) {
        this.trendGraphs = [];
        for (let i = 0; i < graphs.length; i++) {
            if (this.isInGraph(this.channel, graphs[i])) {
                this.trendGraphs.push(graphs[i]);
            }
        }
    }

    isInGraph(channel: Channel, graph: TrendGraphView) {
        return (
            graph.channels.find((v) => v.fcuChannelId == channel.fcuData.fcuChannelId) !== undefined
        );
    }

    toTrendGraphView(id: string) {
        this.navigationService.toTrendGraphView(id);
    }

    toMimicView(id: string) {
        this.navigationService.toMimic(id);
    }
}
